import { FC, ReactNode, useMemo } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useWatch } from 'antd/es/form/Form'
import cn from 'classnames'
import { InputNew } from '@UI/InputNew'
import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import LessonCountLinkFields from '../LessonCountLinkFields/LessonCountLinkFields'
import CreateLinkSectionTitle from '../../CreateLinkSectionTitle'
import CreateLinkNumberInput, {
  createLinkNumberRuleTransform,
} from '../../CreateLinkNumberInput'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../tariffTypes'
import { getCreateLinkBlockTariffTypesHint } from '../../createLinkBlockInfoContent'
import infoClasses from '../../createLinkBlockInfo.module.css'
import type { CourseLinkGroupOption } from '../CourseLinkGroupSelect/CourseLinkGroupSelect'
import {
  formatGroupPerLessonDuration,
  formatGroupPerLessonPrice,
  getGroupPerLessonDefaultsFromGroup,
  getGroupPerLessonLinkFieldDefaults,
  isSameGroupPerLessonParams,
  type GroupPerLessonLessonParams,
} from './groupPerLessonLink.utils'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'
import classes from './index.module.css'

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_funnel_id'>

type GroupPerLessonLinkFieldsProps = {
  form: FormInstance
  tariff?: ICourseTariff | null
  selectedGroup?: CourseLinkGroupOption | null
  /** Чекбокс/селект группы — между полями и таблицей источников. */
  groupSelect?: ReactNode
}

const NO_GROUP_TOOLTIP =
  'Группа не выбрана. Включите чекбокс «Распределить ученика в группу» и выберите группу в селекторе.'

const TARIFF_LOCKED_BY_GROUP_TOOLTIP =
  'При выбранной группе длительность берётся из группы. Можно изменить только стоимость занятия.'

const DURATION_LOCKED_BY_GROUP_TOOLTIP =
  'Длительность задаётся выбранной группой и не редактируется'

const ALREADY_MATCHES_TARIFF_TOOLTIP =
  'В полях уже указаны те же длительность и стоимость, что в тарифе'

/** Tooltip для disabled «Заполнить» в строке группы, когда цена уже совпадает. */
const ALREADY_MATCHES_GROUP_PRICE_TOOLTIP =
  'Стоимость уже совпадает со значением из группы'

const SOURCES_TABLE_INFO = (
  <div className={infoClasses.root}>
    <div className={infoClasses.description}>
      Здесь показаны длительность и цена из тарифа и из выбранной группы.
      Кнопка «Заполнить» копирует их в поля выше.
    </div>
    <div className={infoClasses.description} style={{ marginTop: 6 }}>
      Без группы можно менять оба поля или взять значения из тарифа.
    </div>
    <div className={infoClasses.description} style={{ marginTop: 6 }}>
      С группой длительность берётся из группы и не меняется — править можно
      только цену.
    </div>
  </div>
)

type FillButtonProps = {
  disabled: boolean
  disabledReason?: string
  onClick: () => void
}

const FillButton: FC<FillButtonProps> = ({
  disabled,
  disabledReason,
  onClick,
}) => {
  const button = (
    <Button
      type="button"
      color="gray"
      className={classes.fillButton}
      disabled={disabled}
      onClick={onClick}
    >
      Заполнить
    </Button>
  )

  if (!disabled || !disabledReason) {
    return button
  }

  return (
    <TooltipWrapper title={disabledReason}>
      <span className={classes.fillButtonWrap}>{button}</span>
    </TooltipWrapper>
  )
}

/**
 * UI-блок CRM-ссылки для group_per_lesson:
 * без группы — длительность и цена редактируются;
 * с группой — длительность из группы (locked), цена редактируется.
 */
const GroupPerLessonLinkFields: FC<GroupPerLessonLinkFieldsProps> = ({
  form,
  tariff,
  selectedGroup = null,
  groupSelect = null,
}) => {
  const lessonDurationWatch = useWatch('lesson_duration', form) as
    | number
    | null
  const firstPriceWatch = useWatch('first_price', form) as number | null
  const hasSelectedGroup = Boolean(selectedGroup)

  const currentParams = useMemo<GroupPerLessonLessonParams>(
    () => ({
      lesson_duration:
        lessonDurationWatch != null && Number.isFinite(Number(lessonDurationWatch))
          ? Number(lessonDurationWatch)
          : null,
      first_price:
        firstPriceWatch != null && Number.isFinite(Number(firstPriceWatch))
          ? Number(firstPriceWatch)
          : null,
    }),
    [firstPriceWatch, lessonDurationWatch],
  )

  const tariffParams = useMemo(
    () => getGroupPerLessonLinkFieldDefaults(tariff),
    [tariff],
  )

  const groupParams = useMemo(() => {
    if (!selectedGroup) return null
    return getGroupPerLessonDefaultsFromGroup(tariff, selectedGroup)
  }, [selectedGroup, tariff])

  const groupDisplayParams = useMemo<GroupPerLessonLessonParams | null>(() => {
    if (!selectedGroup) return null
    const duration = Number(selectedGroup.lessonDuration)
    const price = Number(selectedGroup.studentLessonPrice)
    return {
      lesson_duration:
        Number.isFinite(duration) && duration >= 1 ? Math.trunc(duration) : null,
      first_price: Number.isFinite(price) ? price : null,
    }
  }, [selectedGroup])

  const matchesTariff = isSameGroupPerLessonParams(currentParams, tariffParams)
  const matchesGroupPrice =
    groupParams != null &&
    Number.isFinite(Number(currentParams.first_price)) &&
    Number.isFinite(Number(groupParams.first_price)) &&
    Number(currentParams.first_price) === Number(groupParams.first_price)

  const applyTariffParams = () => {
    form.setFieldsValue(tariffParams)
  }

  /** При выбранной группе длительность уже из группы — подставляем только цену. */
  const applyGroupPrice = () => {
    if (!groupParams) return
    form.setFieldsValue({ first_price: groupParams.first_price })
  }

  const renderValues = (
    params: GroupPerLessonLessonParams | null,
  ): ReactNode => {
    if (!params) {
      return (
        <>
          <span className={classes.sourceMuted}>—</span>
          <span className={classes.sourceMuted}>—</span>
        </>
      )
    }

    return (
      <>
        <span>{formatGroupPerLessonDuration(params.lesson_duration)}</span>
        <span>{formatGroupPerLessonPrice(params.first_price)}</span>
      </>
    )
  }

  const tariffRowDisabled = hasSelectedGroup
  const tariffFillDisabled = hasSelectedGroup || matchesTariff
  const tariffFillReason = hasSelectedGroup
    ? TARIFF_LOCKED_BY_GROUP_TOOLTIP
    : matchesTariff
    ? ALREADY_MATCHES_TARIFF_TOOLTIP
    : undefined

  const groupFillDisabled = !hasSelectedGroup || matchesGroupPrice
  const groupFillReason = hasSelectedGroup
    ? matchesGroupPrice
      ? ALREADY_MATCHES_GROUP_PRICE_TOOLTIP
      : undefined
    : undefined

  const groupRow = (
    <div
      className={cn(
        classes.sourcesRow,
        !hasSelectedGroup && classes.sourcesRowDisabled,
      )}
    >
      <span className={classes.sourceName}>Группа</span>
      {renderValues(hasSelectedGroup ? groupDisplayParams : null)}
      <FillButton
        disabled={groupFillDisabled}
        disabledReason={groupFillReason}
        onClick={applyGroupPrice}
      />
    </div>
  )

  const tariffRow = (
    <div
      className={cn(
        classes.sourcesRow,
        tariffRowDisabled && classes.sourcesRowDisabled,
      )}
    >
      <span className={classes.sourceName}>Тариф курса</span>
      {renderValues(tariffParams)}
      <FillButton
        disabled={tariffFillDisabled}
        disabledReason={
          tariffRowDisabled
            ? undefined
            : tariffFillReason
        }
        onClick={applyTariffParams}
      />
    </div>
  )

  return (
    <>
      <CreateLinkSectionTitle
        title="Параметры занятия"
        infoText={getCreateLinkBlockTariffTypesHint(
          CREATE_LINK_BLOCK_TARIFF_TYPES.groupPerLesson,
        )}
      />

      {hasSelectedGroup ? (
        <div className={classes.modeHint}>
          Группа выбрана: длительность берётся из группы, изменить можно только
          стоимость
        </div>
      ) : null}

      <div className={sclasses.inputWithLabel}>
        {hasSelectedGroup ? (
          <TooltipWrapper title={DURATION_LOCKED_BY_GROUP_TOOLTIP}>
            <div className={classes.disabledFieldWrap}>
              <FormItem<FieldType>
                name="lesson_duration"
                rules={[
                  {
                    required: true,
                    message: 'Укажите длительность занятия',
                  },
                  {
                    type: 'number',
                    min: 1,
                    max: 1440,
                    message: 'Длительность: от 1 до 1440 минут',
                    transform: createLinkNumberRuleTransform,
                  },
                ]}
                noStyle
              >
                <CreateLinkNumberInput
                  className={cn(spclasses.input, spclasses.disabledInput)}
                  title="Длительность занятия (мин.)"
                  integer
                  min={1}
                  max={1440}
                  placeholder={60}
                  disabled
                />
              </FormItem>
            </div>
          </TooltipWrapper>
        ) : (
          <FormItem<FieldType>
            name="lesson_duration"
            rules={[
              {
                required: true,
                message: 'Укажите длительность занятия',
              },
              {
                type: 'number',
                min: 1,
                max: 1440,
                message: 'Длительность: от 1 до 1440 минут',
                transform: createLinkNumberRuleTransform,
              },
            ]}
            noStyle
          >
            <CreateLinkNumberInput
              className={spclasses.input}
              title="Длительность занятия (мин.)"
              integer
              min={1}
              max={1440}
              placeholder={60}
            />
          </FormItem>
        )}
      </div>

      <div className={sclasses.inputWithLabel}>
        <FormItem<FieldType>
          name="first_price"
          rules={[
            {
              required: true,
              message: 'Укажите стоимость одного занятия',
            },
            {
              type: 'number',
              min: 0,
              message: 'Цена не может быть отрицательной',
              transform: createLinkNumberRuleTransform,
            },
          ]}
          noStyle
        >
          <CreateLinkNumberInput
            className={spclasses.input}
            title="Стоимость одного занятия (₽)"
            min={0}
            placeholder={0}
          />
        </FormItem>
      </div>

      {groupSelect ? (
        <div className={classes.groupSelectSlot}>{groupSelect}</div>
      ) : null}

      <CreateLinkSectionTitle
        title="Источники длительности и стоимости"
        infoText={SOURCES_TABLE_INFO}
        className={classes.sourcesTitle}
      />
      <div className={classes.sourcesTable}>
        <div className={classes.sourcesHead}>
          <span>Источник</span>
          <span>Длительность</span>
          <span>Стоимость</span>
          <span />
        </div>
        {tariffRowDisabled ? (
          <TooltipWrapper title={TARIFF_LOCKED_BY_GROUP_TOOLTIP}>
            <div className={classes.tooltipRow}>{tariffRow}</div>
          </TooltipWrapper>
        ) : (
          tariffRow
        )}
        {hasSelectedGroup ? (
          groupRow
        ) : (
          <TooltipWrapper title={NO_GROUP_TOOLTIP}>
            <div className={classes.tooltipRow}>{groupRow}</div>
          </TooltipWrapper>
        )}
      </div>

      <LessonCountLinkFields form={form} />
    </>
  )
}

export default GroupPerLessonLinkFields
